import { Fragment, useEffect, useState } from 'react'
import { sampleApiClient } from '../api/sampleApp'
import { usePorthContext } from '../context/PorthContext'
import { PERMISSIONS } from '../constants'
import Fingerprint, { hasFingerprint, type FingerprintFields } from '../components/Fingerprint'

interface Bill extends FingerprintFields {
  bill_id: string
  vendor_name: string
  amount: string
  status: string
  due_date: string
}

const STATUS_BADGE: Record<string, string> = {
  // PORTH-597 — a record waiting on someone else.
  pending_approval: 'bg-amber-100 text-amber-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_BADGE[status] ?? 'bg-gray-100 text-gray-800'
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  )
}

export default function APPage() {
  const { currentUser } = usePorthContext()
  const [bills, setBills] = useState<Bill[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({ vendor_name: '', amount: '', due_date: '' })

  const canWrite = currentUser?.permissions?.includes(PERMISSIONS.AP_BILLS_WRITE) ?? false

  // PORTH-597 — the step that did not exist. A bill was created and then
  // had nowhere to go: the approvals screen queried a prefix nothing wrote, so
  // nothing could ever appear for a decision. Submitting is guarded by this
  // page's own write permission, not by approvals.write — you submit your own
  // work, someone else decides on it.
  const [submittingId, setSubmittingId] = useState<string | null>(null)

  function submitForApproval(id: string) {
    setSubmittingId(id)
    setError(null)
    sampleApiClient
      .post<{ status: string }>(`/sample/ap/bills/${id}/submit`)
      .then(() => fetchBills())
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setSubmittingId(null))
  }

  function fetchBills() {
    setLoading(true)
    setError(null)
    sampleApiClient
      .get<Bill[]>('/sample/ap/bills')
      .then(r => setBills(r.data))
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!currentUser) return
    fetchBills()
  }, [currentUser])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    sampleApiClient
      .post<Bill>('/sample/ap/bills', {
        vendor_name: form.vendor_name,
        amount: parseFloat(form.amount),
        due_date: form.due_date,
      })
      .then(() => {
        setIsOpen(false)
        setForm({ vendor_name: '', amount: '', due_date: '' })
        fetchBills()
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setSubmitting(false))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-gray-900">Accounts Payable</h1>
        {canWrite && (
          <button
            onClick={() => setIsOpen(true)}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            New Bill
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-6">Vendor bills, outgoing payments, vendor balances</p>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        ) : bills.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No bills found</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Bill ID', 'Vendor', 'Amount', 'Status', 'Due Date', ...(canWrite ? ['Actions'] : [])].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bills.map(bill => (
                <Fragment key={bill.bill_id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono text-gray-500">{bill.bill_id.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{bill.vendor_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">£{parseFloat(bill.amount).toFixed(2)}</td>
                  <td className="px-4 py-3"><StatusBadge status={bill.status} /></td>
                  <td className="px-4 py-3 text-sm text-gray-500">{bill.due_date}</td>
                  {canWrite && (
                    <td className="px-4 py-3">
                      {bill.status === 'pending' ? (
                        <button
                          onClick={() => submitForApproval(bill.bill_id)}
                          disabled={submittingId === bill.bill_id}
                          className="rounded px-2.5 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 disabled:opacity-50"
                        >
                          {submittingId === bill.bill_id ? 'Submitting\u2026' : 'Submit for approval'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {bill.status === 'pending_approval' ? 'awaiting approval' : '\u2014'}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
                {hasFingerprint(bill) && (
                  <tr className="bg-gray-50/60">
                    <td colSpan={canWrite ? 6 : 5} className="px-4 pb-3 pt-0">
                      <Fingerprint record={bill} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">New Bill</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
                <input
                  required
                  type="text"
                  value={form.vendor_name}
                  onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
